
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { NavLink as RouterLink, useLocation, matchPath } from 'react-router-dom';

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  Box,
  Collapse,
  List,
  ListItemText,
  Tooltip,
} from '@mui/material';

import { StyledNavItem, StyledNavItemIcon } from './styles';

NavSection.propTypes = {
  data: PropTypes.array,
  openmenu: PropTypes.bool,
};

export default function NavSection({ data = [], openmenu, ...other }) {
  return (
    <Box {...other}>
      <List disablePadding sx={{ p: openmenu ? 0 : 1 }}>
        {data.map((item) => (
          <NavItem key={item.title} item={item} openmenu={openmenu} />
        ))}
      </List>
    </Box>
  );
}

NavItem.propTypes = {
  item: PropTypes.object,
  openmenu: PropTypes.bool,
};

function NavItem({ item, openmenu }) {
  const location = useLocation();
  const { title, icon, info, child, path } = item;
  const hasChildren = Array.isArray(child) && child.length > 0;

  const isChildActive = hasChildren
    ? child.some(
        (subitem) =>
          subitem.path &&
          matchPath(
            { path: subitem.path, end: true },
            location.pathname
          )
      )
    : false;

  const isParentActive =
    !hasChildren &&
    Boolean(
      path &&
        matchPath(
          { path, end: true },
          location.pathname
        )
    );

  const [open, setOpen] = useState(isChildActive);

  useEffect(() => {
    if (isChildActive) {
      setOpen(true);
    }
  }, [isChildActive]);

  const handleClick = () => {
    if (hasChildren) {
      setOpen((previous) => !previous);
    }
  };

  const activeSx = {
    '&.active': {
      color: '#FFFFFF',
      bgcolor: '#3478F6',
      fontWeight: 'fontWeightBold',
      boxShadow: '0 3px 8px rgba(53,120,246,0.2)',
      '&:hover': {
        bgcolor: '#2868E8',
      },
    },
  };

  const collapsedItemSx = openmenu
    ? {
        width: '100%',
        minWidth: 0,
        px: 0,
        justifyContent: 'center',
      }
    : {};

  const parentButton = (
    <StyledNavItem
      component={hasChildren ? 'button' : RouterLink}
      {...(!hasChildren && path
        ? {
            to: path,
            end: true,
            className: isParentActive ? 'active' : '',
          }
        : {})}
      onClick={hasChildren ? handleClick : undefined}
      aria-expanded={hasChildren ? open : undefined}
      aria-label={openmenu ? title : undefined}
      sx={{
        ...collapsedItemSx,
        ...(hasChildren
          ? {
              width: '100%',
              border: 0,
              textAlign: 'left',
              cursor: 'pointer',
              color: '#A8B3C7',
              bgcolor: 'transparent',
              boxShadow: 'none',
              '&:hover': {
                color: '#FFFFFF',
                bgcolor: 'rgba(255,255,255,0.07)',
              },
            }
          : activeSx),
      }}
    >
      <StyledNavItemIcon>{icon || null}</StyledNavItemIcon>

      {!openmenu && (
        <ListItemText disableTypography primary={title} />
      )}

      {!openmenu && hasChildren && (
        <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
          {open ? <ExpandLess /> : <ExpandMore />}
        </Box>
      )}

      {!openmenu && (info || null)}
    </StyledNavItem>
  );

  return (
    <>
      {openmenu ? (
        <Tooltip title={title} placement="right" arrow>
          {parentButton}
        </Tooltip>
      ) : (
        parentButton
      )}

      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List
            component="div"
            disablePadding
            sx={{
              mt: 0.5,
              // Indent submenu items even when the sidebar is collapsed.
              ml: openmenu ? 0 : 2,
              pl: openmenu ? 1.5 : 1,
              borderLeft: '2px solid rgba(255,255,255,0.16)',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            {child.map((subitem) => {
              const submenuButton = (
                <StyledNavItem
                  component={RouterLink}
                  to={subitem.path}
                  end
                  key={subitem.title}
                  sx={{
                    ...(openmenu
                      ? {
                          width: '100%',
                          minWidth: 0,
                          pl: 1.5,
                          pr: 0,
                          justifyContent: 'flex-start',
                        }
                      : {
                          width: '100%',
                        }),
                    ...activeSx,
                  }}
                >
                  <StyledNavItemIcon>
                    {subitem.icon || null}
                  </StyledNavItemIcon>

                  {!openmenu && (
                    <ListItemText disableTypography>
                      {subitem.title}
                    </ListItemText>
                  )}
                </StyledNavItem>
              );

              return (
                <Box key={subitem.title} sx={{ width: '100%' }}>
                  {openmenu ? (
                    <Tooltip
                      title={subitem.title}
                      placement="right"
                      arrow
                    >
                      {submenuButton}
                    </Tooltip>
                  ) : (
                    submenuButton
                  )}
                </Box>
              );
            })}
          </List>
        </Collapse>
      )}
    </>
  );
}
